<template>
  <div class="image-viewer" ref="viewer" tabindex="0">
    <!-- 单页图片容器 -->
    <div
      class="page-wrapper"
      ref="pageWrapper"
      @mousedown="onDragStart"
      @mousemove="onDragMove"
      @mouseup="onDragEnd"
      @mouseleave="onDragEnd"
      @touchstart.prevent="onTouchStart"
      @touchmove.prevent="onTouchMove"
      @touchend="onDragEnd"
      @wheel.prevent="onWheel"
      @dblclick="onDblClick"
    >
      <!-- 当前单页图片 -->
      <div class="page-single" ref="currentPage" :style="currentPageStyle">
        <img
          v-if="currentImg"
          :src="currentImg"
          class="page-img"
          @load="onImageLoaded"
          @error="onImageError"
          alt=""
          draggable="false"
        />
      </div>

      <!-- 未缩放时的点击区域 -->
      <template v-if="scale <= 1">
        <div class="click-zone left-zone" @click.stop="goPrev" v-if="currentIndex > 0"></div>
        <div class="click-zone right-zone" @click.stop="goNext" v-if="currentIndex < imgs.length - 1"></div>
      </template>

      <!-- 缩放提示 -->
      <div v-if="isZoomed" class="zoom-indicator">
        {{ Math.round(scale * 100) }}% — 拖拽移动 · 滚轮缩放 · 双击还原
      </div>

      <!-- 加载指示器 -->
      <div v-if="imageLoading" class="loading-indicator">
        <div class="spinner"></div>
      </div>
    </div>

    <!-- 左右两侧翻页箭头按钮 -->
    <div
      class="side-arrow arrow-left"
      :class="{ hidden: currentIndex === 0 || isZoomed }"
      @click.stop="goPrev"
      title="上一页"
    >
      <span>◀</span>
    </div>
    <div
      class="side-arrow arrow-right"
      :class="{ hidden: currentIndex >= imgs.length - 1 || isZoomed }"
      @click.stop="goNext"
      title="下一页"
    >
      <span>▶</span>
    </div>

    <!-- 底部控制栏 -->
    <div class="controls" v-if="imgs && imgs.length > 0">
      <button class="ctrl-btn" @click="goFirst" :disabled="currentIndex === 0" title="首页">⏮</button>
      <button class="ctrl-btn" @click="goPrev" :disabled="currentIndex === 0" title="上一页">◀</button>
      <span class="page-info">{{ currentIndex + 1 }} / {{ imgs.length }}</span>
      <button class="ctrl-btn" @click="goNext" :disabled="currentIndex >= imgs.length - 1" title="下一页">▶</button>
      <button class="ctrl-btn" @click="goLast" :disabled="currentIndex >= imgs.length - 1" title="尾页">⏭</button>

      <span class="ctrl-sep">|</span>

      <button class="ctrl-btn" @click="zoomOut" title="缩小">−</button>
      <span class="zoom-info">{{ Math.round(scale * 100) }}%</span>
      <button class="ctrl-btn" @click="zoomIn" title="放大">+</button>
      <button class="ctrl-btn" @click="resetZoom" title="适应屏幕">⊡</button>

      <span class="ctrl-sep">|</span>

      <button class="ctrl-btn auto-btn" @click="toggleAutoPlay" :title="autoPlaying ? '暂停' : '自动播放'">
        {{ autoPlaying ? '⏸ 暂停' : '▶ 播放' }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      imgs: [],
      currentIndex: 0,           // 当前图片索引
      imageLoading: true,

      // 缩放
      scale: 1,
      zoomTX: 0,
      zoomTY: 0,
      isZoomDragging: false,
      zStartX: 0, zStartY: 0, zStartTX: 0, zStartTY: 0,

      // 拖拽（未缩放时左右滑动翻页）
      isDragging: false,
      hasDragged: false,
      dragStartX: 0,
      dragStartY: 0,

      // 自动播放
      autoPlaying: false,
      autoPlayTimer: null,

      // 预加载
      preloadCache: {},
    };
  },

  computed: {
    currentImg() {
      return this.imgs[this.currentIndex] || null;
    },

    currentPageStyle() {
      const s = {};
      if (this.scale !== 1 || this.zoomTX !== 0 || this.zoomTY !== 0) {
        s.transform = `translate(${this.zoomTX}px, ${this.zoomTY}px) scale(${this.scale})`;
        s.transition = this.isZoomDragging ? 'none' : 'transform 0.2s ease';
        s.cursor = this.scale > 1 ? (this.isZoomDragging ? 'grabbing' : 'grab') : 'default';
        return s;
      }
      s.transform = '';
      s.transition = '';
      s.cursor = 'default';
      return s;
    },

    isZoomed() {
      return this.scale > 1;
    },
  },

  watch: {
    '$route.query': {
      immediate: true,
      handler(val) {
        console.log(val, 'val');
        if (!val) return;
        let arr = val.imgs;
        // 如果 URL 里没有 imgs，尝试通过 key 从 sessionStorage 读取（处理大数组 URL 超长问题）
        if (!arr && val.key) {
          const stored = sessionStorage.getItem(val.key);
          if (stored) {
            try { arr = JSON.parse(stored); } catch (e) { arr = null; }
            sessionStorage.removeItem(val.key); // 用完即删
          }
        }
        if (!arr) return;
        if (typeof arr === 'string') {
          try { arr = JSON.parse(arr); } catch (e) { arr = [arr]; }
        }
        if (Array.isArray(arr) && arr.length > 0) {
          this.imgs = arr;
          this.currentIndex = 0;
          this.resetZoom();
          this.imageLoading = true;
          this.preloadAround(0);
        }
      },
    },
  },

  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
    this.stopAutoPlay();
  },

  methods: {
    /* ==================== 键盘 ==================== */
    handleKeydown(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const k = e.key;
      if (k === 'ArrowLeft' || k === 'ArrowUp' || k === 'PageUp') { e.preventDefault(); this.goPrev(); }
      else if (k === 'ArrowRight' || k === 'ArrowDown' || k === 'PageDown') { e.preventDefault(); this.goNext(); }
      else if (k === 'Home') { e.preventDefault(); this.goFirst(); }
      else if (k === 'End') { e.preventDefault(); this.goLast(); }
      else if (k === ' ') { e.preventDefault(); this.toggleAutoPlay(); }
      else if (k === '+' || k === '=') { e.preventDefault(); this.zoomIn(); }
      else if (k === '-') { e.preventDefault(); this.zoomOut(); }
      else if (k === '0') { e.preventDefault(); this.resetZoom(); }
    },

    /* ==================== 预加载 ==================== */
    preloadOne(url) {
      if (!url || this.preloadCache[url]) return;
      const img = new Image();
      img.src = url;
      this.preloadCache[url] = img;
    },

    // 只预加载当前索引前后 PRELOAD_RADIUS 张，翻页时滑动窗口跟进
    preloadAround(index, radius = 3) {
      const start = Math.max(0, index - radius);
      const end = Math.min(this.imgs.length - 1, index + radius);
      for (let i = start; i <= end; i++) {
        this.preloadOne(this.imgs[i]);
      }
    },

    /* ==================== 图片加载 ==================== */
    onImageLoaded() {
      this.imageLoading = false;
    },

    onImageError() {
      this.imageLoading = false;
      console.warn('图片加载失败');
    },

    /* ==================== 导航 ==================== */
    goFirst() {
      if (this.currentIndex !== 0) {
        this.currentIndex = 0;
        this.imageLoading = true;
        this.resetZoom();
        this.preloadAround(0);
      }
    },
    goLast() {
      const last = this.imgs.length - 1;
      if (this.currentIndex !== last) {
        this.currentIndex = last;
        this.imageLoading = true;
        this.resetZoom();
        this.preloadAround(last);
      }
    },
    goPrev() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
        this.imageLoading = true;
        this.resetZoom();
        this.preloadAround(this.currentIndex);
      }
    },
    goNext() {
      if (this.currentIndex < this.imgs.length - 1) {
        this.currentIndex++;
        this.imageLoading = true;
        this.resetZoom();
        this.preloadAround(this.currentIndex);
      }
    },

    /* ==================== 拖拽 ==================== */
    onDragStart(e) {
      if (this.scale > 1) {
        this.isZoomDragging = true;
        this.zStartX = e.clientX;
        this.zStartY = e.clientY;
        this.zStartTX = this.zoomTX;
        this.zStartTY = this.zoomTY;
        return;
      }
      this.dragStartX = e.clientX;
      this.dragStartY = e.clientY;
      this.isDragging = true;
      this.hasDragged = false;
    },

    onDragMove(e) {
      if (this.isZoomDragging) {
        this.zoomTX = this.zStartTX + (e.clientX - this.zStartX);
        this.zoomTY = this.zStartTY + (e.clientY - this.zStartY);
        return;
      }
      if (!this.isDragging) return;

      const dx = e.clientX - this.dragStartX;
      const dy = e.clientY - this.dragStartY;

      if (!this.hasDragged) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        if (Math.abs(dy) > Math.abs(dx) * 1.3) { this.isDragging = false; return; }
        this.hasDragged = true;
      }
      // 拖拽时不做实时翻页，只在 dragEnd 时判断
    },

    onDragEnd(e) {
      if (this.isZoomDragging) { this.isZoomDragging = false; return; }
      if (!this.isDragging) return;
      this.isDragging = false;

      if (!this.hasDragged) {
        // 未拖拽，判断点击位置
        if (e && e.clientX !== undefined) {
          const rect = this.$refs.pageWrapper.getBoundingClientRect();
          const x = e.clientX - rect.left;
          if (x < rect.width / 2) this.goPrev();
          else this.goNext();
        }
        return;
      }

      // 拖拽距离超过阈值则翻页
      const dx = e.clientX - this.dragStartX;
      if (Math.abs(dx) > 80) {
        if (dx < 0) this.goNext();
        else this.goPrev();
      }
    },

    /* 触摸 */
    onTouchStart(e) {
      const t = e.touches[0];
      this.onDragStart({ clientX: t.clientX, clientY: t.clientY });
    },
    onTouchMove(e) {
      const t = e.touches[0];
      this.onDragMove({ clientX: t.clientX, clientY: t.clientY });
    },

    /* ==================== 缩放 ==================== */
    zoomIn()  { this.scale = Math.min(this.scale * 1.3, 8); this.clampZoom(); },
    zoomOut() {
      this.scale = Math.max(this.scale / 1.3, 0.3);
      if (this.scale <= 1) { this.zoomTX = 0; this.zoomTY = 0; }
      this.clampZoom();
    },
    resetZoom()      { this.scale = 1; this.zoomTX = 0; this.zoomTY = 0; },
    clampZoom()       { const L = 1200; this.zoomTX = Math.max(-L, Math.min(L, this.zoomTX)); this.zoomTY = Math.max(-L, Math.min(L, this.zoomTY)); },

    onWheel(e) {
      const d = e.deltaY > 0 ? 0.92 : 1.08;
      const ns = Math.max(0.3, Math.min(8, this.scale * d));
      const rect = this.$refs.pageWrapper.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const mx = e.clientX - cx, my = e.clientY - cy;
      const r = ns / this.scale;
      this.zoomTX = mx - r * (mx - this.zoomTX);
      this.zoomTY = my - r * (my - this.zoomTY);
      this.scale = ns;
      if (this.scale <= 1) { this.zoomTX = 0; this.zoomTY = 0; }
      this.clampZoom();
    },

    onDblClick() {
      if (this.scale > 1.05) this.resetZoom();
      else this.scale = 2;
    },

    /* ==================== 自动播放 ==================== */
    toggleAutoPlay() { this.autoPlaying ? this.stopAutoPlay() : this.startAutoPlay(); },
    startAutoPlay() {
      this.autoPlaying = true;
      this.autoPlayTimer = setInterval(() => {
        if (this.currentIndex < this.imgs.length - 1) this.goNext();
        else this.stopAutoPlay();
      }, 3000);
    },
    stopAutoPlay() {
      this.autoPlaying = false;
      if (this.autoPlayTimer) { clearInterval(this.autoPlayTimer); this.autoPlayTimer = null; }
    },
  },
};
</script>

<style scoped>
* { margin: 0; padding: 0; box-sizing: border-box; }

/* ==================== 全屏 ==================== */
.image-viewer {
  position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
  background: #3a3a3a;
  outline: none; overflow: hidden; user-select: none;
}

/* ==================== 单页容器 ==================== */
.page-wrapper {
  position: absolute; top: 0; left: 0; right: 0; bottom: 60px;
  overflow: hidden;
  background: #3a3a3a;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-single {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 20px;
}

.page-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  pointer-events: none;
  filter: drop-shadow(0 1px 4px rgba(0,0,0,0.18));
  background: transparent;
}

/* ==================== 加载 ==================== */
.loading-indicator { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); z-index: 10; }
.spinner {
  width: 44px; height: 44px;
  border: 3px solid rgba(255,255,255,0.15); border-top-color: #bf1c29;
  border-radius: 50%; animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ==================== 点击区域 ==================== */
.click-zone { position: absolute; top: 0; bottom: 0; width: 30%; z-index: 5; background: transparent; }
.left-zone  { left: 0; }
.right-zone { right: 0; }

/* ==================== 左右两侧翻页箭头按钮 ==================== */
.side-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 80px;
  height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 25;
  cursor: pointer;
  opacity: 0.65;
  transition: opacity 0.2s, background 0.2s, transform 0.2s;
  user-select: none;
  border-radius: 10px;
}
.side-arrow:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.5);
  transform: translateY(-50%) scale(1.06);
}
.side-arrow:active {
  background: rgba(0, 0, 0, 0.65);
  transform: translateY(-50%) scale(0.94);
}
.side-arrow.hidden {
  opacity: 0;
  pointer-events: none;
}
.side-arrow span {
  font-size: 72px;
  font-weight: bold;
  color: #eee;
  line-height: 1;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.6);
  transition: color 0.2s;
}
.side-arrow:hover span {
  color: #fff;
}
.arrow-left  { left: 80px; }
.arrow-right { right: 80px; }

/* ==================== 缩放提示 ==================== */
.zoom-indicator {
  position: absolute; top: 16px; left: 50%; transform: translateX(-50%);
  background: rgba(0,0,0,0.7); color: #ccc; font-size: 13px;
  padding: 6px 18px; border-radius: 20px; z-index: 10;
  pointer-events: none; letter-spacing: 0.5px;
}

/* ==================== 底部控制栏 ==================== */
.controls {
  position: absolute; bottom: 0; left: 0; right: 0; height: 60px;
  background: rgba(0,0,0,0.88); display: flex;
  align-items: center; justify-content: center; gap: 10px;
  z-index: 20; backdrop-filter: blur(6px);
}
.ctrl-btn {
  background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.18);
  color: #eee; font-size: 15px; min-width: 36px; height: 34px;
  border-radius: 5px; cursor: pointer; display: flex;
  align-items: center; justify-content: center; padding: 0 7px;
  transition: all 0.15s;
}
.ctrl-btn:hover:not(:disabled) { background: rgba(255,255,255,0.22); border-color: rgba(255,255,255,0.35); color: #fff; }
.ctrl-btn:disabled { opacity: 0.28; cursor: not-allowed; }
.ctrl-btn.auto-btn { min-width: 72px; font-size: 13px; letter-spacing: 0.5px; }
.ctrl-sep { color: rgba(255,255,255,0.2); font-size: 16px; user-select: none; }
.page-info, .zoom-info {
  color: #ccc; font-size: 13px; min-width: 64px;
  text-align: center; user-select: none; letter-spacing: 0.5px;
  font-variant-numeric: tabular-nums;
}
</style>
